import { useState } from "react";

import { App, Spin } from "antd";
import { useTranslation } from "react-i18next";

import BaseButton from "@/components_v2/Button/BaseButton";
import BaseStepper from "@/components_v2/Stepper/BaseStepper";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";
import { useAdminInviteRegisterForm } from "../hooks/useAdminInviteRegisterForm";
import { useRegisterSubmit } from "../hooks/useRegisterSubmit";
import PersonalDetailsStep from "./form-steps/PersonalDetailsStep";
import OrganizationDetailsStep from "./form-steps/OrganizationDetailsStep";
import InviteTeamStep from "./form-steps/InviteTeamStep";
import OrganizationUserPermissionsModal from "./OrganizationUserPermissionsModal";
import AlternateAction from "@/layouts/OnboardingLayout/AlternateAction";
import { AdminRegisterFormValues, RegisterStep } from "../types";
import {
  INVITE_TEAM_VALIDATION_FIELDS,
  ORGANIZATION_DETAILS_VALIDATION_FIELDS,
  PERSONAL_DETAILS_VALIDATION_FIELDS,
} from "../utils/formValidation";

import "../styles.css";

const AdminInviteRegister = () => {
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [openPermissionsModal, setOpenPermissionsModal] = useState(false);

  const {
    form,
    data,
    isSuccess,
    error,
    isFetching,
    countryOptions,
    areaOfExpertiseOptions,
  } = useAdminInviteRegisterForm();
  const { submit, isLoading } = useRegisterSubmit(form);

  const steps: RegisterStep[] = [
    {
      title: t("auth.labels.yourAccountStep"),
      validationFields: PERSONAL_DETAILS_VALIDATION_FIELDS,
      render: () => (
        <PersonalDetailsStep
          form={form}
          countryOptions={countryOptions}
          areaOfExpertiseOptions={areaOfExpertiseOptions}
          title={t("auth.labels.accountTitle")}
          description={t("auth.labels.adminInviteAccountDescription")}
        />
      ),
    },
    {
      title: t("auth.labels.yourOrganizationStep"),
      validationFields: ORGANIZATION_DETAILS_VALIDATION_FIELDS,
      render: () => (
        <OrganizationDetailsStep
          form={form}
          countryOptions={countryOptions}
          organizationName={data?.organization?.name}
        />
      ),
    },
    {
      title: t("auth.labels.inviteMembersStep"),
      validationFields: INVITE_TEAM_VALIDATION_FIELDS,
      render: () => (
        <InviteTeamStep
          form={form}
          onOpenPermissionsModal={() => setOpenPermissionsModal(true)}
        />
      ),
    },
  ];

  const handleSubmit = (values: AdminRegisterFormValues) => {
    const payload = {
      token: values.token,
      user: values.user,
      profile: values.profile,
      organization: values.organization,
    };

    submit(payload, (submitError) => {
      const firstError = submitError.data?.errors?.[0];
      if (submitError.status !== 400 || !firstError) return;

      const attributeName = firstError.attr;
      if (attributeName) {
        const stepIndex = steps.findIndex((registerStep) =>
          registerStep.validationFields.some(
            (field) => field === attributeName,
          ),
        );

        if (stepIndex >= 0) {
          setStep(stepIndex);
        }
      }

      if (firstError.detail) {
        notification.error({ message: firstError.detail });
      }
    });
  };

  const handleNext = async () => {
    const validationFields = steps[step]?.validationFields;
    const isValid = await form.trigger(validationFields);

    if (!isValid) return;

    if (step === steps.length - 1) {
      handleSubmit(form.getValues());
      return;
    }

    setStep((currentStep) => currentStep + 1);
  };

  const handleBack = () => {
    if (step > 0) {
      setStep((currentStep) => currentStep - 1);
    }
  };

  if (isFetching) {
    return (
      <div className="flex justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!isSuccess) {
    return (
      <div className="text-center">
        {error?.code && (
          <h3 className="register__title">
            {t(`auth.errors.${error.code}`, { defaultValue: error.detail })}
          </h3>
        )}
      </div>
    );
  }

  const isLastStep = step === steps.length - 1;
  const isFirstStep = step === 0;

  return (
    <div className="register-admin-invite-register-container full-width flex flex-col gap-400">
      <BaseStepper
        current={step}
        items={steps.map(({ title }) => ({ title }))}
      />

      {steps[step]?.render()}

      <div className="flex flex-col gap-300">
        {step === 0 ? (
          <BaseButton isFullWidth disabled={isLoading} onClick={handleNext}>
            {t("auth.labels.next")}
          </BaseButton>
        ) : (
          <div className="flex gap-300 full-width">
            <BaseButton
              variant={BUTTON_VARIANTS.TERTIARY}
              className="flex-1"
              disabled={isLoading}
              onClick={handleBack}
            >
              {t("auth.labels.back")}
            </BaseButton>
            <BaseButton
              className="flex-1"
              disabled={isLoading}
              loading={isLoading && isLastStep}
              onClick={handleNext}
            >
              {isLastStep
                ? t("auth.labels.completeSetup")
                : t("auth.labels.next")}
            </BaseButton>
          </div>
        )}

        {isLastStep && (
          <button
            type="button"
            className="register__skip-link body-m-regular"
            onClick={handleNext}
          >
            {t("auth.labels.skipStep")}
          </button>
        )}
      </div>

      {isFirstStep && <AlternateAction />}

      <OrganizationUserPermissionsModal
        open={openPermissionsModal}
        onCancel={() => setOpenPermissionsModal(false)}
      />
    </div>
  );
};

export default AdminInviteRegister;
